import { escreverNaUrl, TAMANHO_PADRAO, type FiltrosDaLista } from '../../api/filtrosDeGmud'
import { TERMOS } from '../../lib/glossario'
import type { OrigemGmud, StatusGmud } from '../../types/gmud'

/**
 * Os recortes que o painel conta, e o endereço da lista que mostra cada um.
 *
 * Não existe endpoint de métricas: cada número desta tela é um `GET /gmuds` com `tamanho=1`, do
 * qual se lê só o `totalElementos` do envelope. É a mesma consulta da listagem, com outro tamanho
 * de página — e é de propósito. O `GmudConsultaJdbc` conta que no sistema anterior a listagem, o
 * painel e a exportação tinham cada um a sua montagem do mesmo filtro, e que "aguardando o comitê"
 * dava três números diferentes em três telas. Aqui o recorte é UM só por cartão: é o que se conta
 * e é o que o link abre, então o número do cartão e o total no rodapé da lista não têm como
 * discordar.
 */

/**
 * Toda contagem do processo exclui o histórico importado, e isso não é escolha de recorte.
 *
 * As 1.027 solicitações trazidas do sistema anterior não passaram por comitê, nem por conferência
 * contra o Termo de Referência, nem por comprovação de teste. Somá-las a um número desta tela
 * faria o painel afirmar aprovações que nunca existiram — e quem lê o painel é a fiscalização do
 * contrato.
 *
 * O filtro é por `origem`, e não por `status`. Hoje todo registro importado está no estado
 * `MIGRADO_V1` e daria para separar pelo estado, mas isso é coincidência da importação e não
 * definição: estado é etapa do processo e muda; origem é fixada na importação e não muda mais.
 */
const SO_O_PROCESSO: OrigemGmud[] = ['V2']

const SO_O_HISTORICO: OrigemGmud[] = ['MIGRADO_V1']

export interface RecorteDoPainel {
  /** Identificador estável. Não é rótulo: trocar o texto do cartão não pode trocar a chave. */
  id: string
  rotulo: string
  /** Uma linha dizendo o que o recorte significa, para quem não é de TI. */
  ajuda: string
  /** Vazio = todos os estados. */
  status: StatusGmud[]
  origem: OrigemGmud[]
  /**
   * Cor do acento do cartão, na mesma família que a etiqueta daquele estado usa em `Etiqueta.tsx`.
   * Sem essa amarração, o âmbar do cartão "aguardando o comitê" abriria uma lista de etiquetas
   * verdes, e a cor deixaria de querer dizer alguma coisa de uma tela para a outra.
   */
  acento: string
}

/** Já enviadas, esperando o comitê. Os dois estados são a mesma espera para quem acompanha. */
export const AGUARDANDO_COMITE: RecorteDoPainel = {
  id: 'aguardando-comite',
  rotulo: 'Aguardando o comitê',
  ajuda: `Já foram enviadas e esperam entrar em pauta ou sair da avaliação do ${TERMOS.cab.rotulo}.`,
  status: ['SOLICITADA', 'EM_AVALIACAO_CAB'],
  origem: SO_O_PROCESSO,
  acento: 'bg-amber-400',
}

/** O comitê devolveu: a bola está com quem abriu a solicitação. */
export const AGUARDANDO_AJUSTES: RecorteDoPainel = {
  id: 'aguardando-ajustes',
  rotulo: 'Aguardando ajustes',
  ajuda: 'O comitê pediu informação ou correção. Só voltam à pauta depois de reenviadas.',
  status: ['AGUARDANDO_AJUSTES'],
  origem: SO_O_PROCESSO,
  acento: 'bg-orange-400',
}

/** Têm decisão do comitê e liberação para produção, mas a implantação ainda não começou. */
export const APROVADAS_NAO_EXECUTADAS: RecorteDoPainel = {
  id: 'aprovadas',
  rotulo: 'Aprovadas, ainda não executadas',
  ajuda: 'O comitê aprovou e a implantação ainda não começou. É o que pode ir para produção agora.',
  status: ['APROVADA'],
  // A procedência aqui é o caso mais perigoso de todos, e por isso o teste a cobra em TODO
  // recorte em vez de só onde o número sairia errado hoje: 1.025 dos 1.027 registros importados
  // estão como "Aprovado". Sem este filtro, o cartão que diz "pode ir para produção agora"
  // somaria mil aprovações que nenhum comitê deu.
  origem: SO_O_PROCESSO,
  acento: 'bg-green-500',
}

/**
 * Em curso. Dois estados sob um rótulo só, como no atalho da listagem: entre implantar e validar
 * não há espera de ninguém, e separá-los daria dois cartões que se leem como um.
 */
export const EM_CURSO: RecorteDoPainel = {
  id: 'em-curso',
  rotulo: 'Em execução',
  ajuda: 'Sendo implantadas, ou em verificação depois da implantação.',
  status: ['EM_EXECUCAO', 'EM_VERIFICACAO'],
  origem: SO_O_PROCESSO,
  acento: 'bg-niteroi-500',
}

/** Tudo o que nasceu neste sistema, em qualquer estado. É o denominador honesto do painel. */
export const TODO_O_PROCESSO: RecorteDoPainel = {
  id: 'processo',
  rotulo: 'Solicitações deste sistema',
  ajuda: 'Todas as que foram abertas aqui, em qualquer etapa.',
  status: [],
  origem: SO_O_PROCESSO,
  acento: 'bg-niteroi-700',
}

/**
 * O histórico importado, contado à parte e NUNCA somado aos números acima.
 *
 * Mesmo recorte do item de menu (`/gmuds?origem=MIGRADO_V1`), e tem de continuar sendo: é o que
 * faz o item "Histórico do sistema anterior" acender quando o link do painel abre a lista.
 */
export const HISTORICO_IMPORTADO: RecorteDoPainel = {
  id: 'historico',
  rotulo: TERMOS.historicoMigrado.rotulo,
  ajuda: TERMOS.historicoMigrado.ajuda,
  status: [],
  origem: SO_O_HISTORICO,
  acento: 'bg-slate-400',
}

/**
 * As que estão paradas num estado que depende de alguém agir.
 *
 * `APROVADA` entra porque aprovada e não executada também espera ação — a de implantar dentro da
 * janela. Fica de fora o que já está andando (execução e verificação) e o que acabou.
 */
export const ESPERANDO_ALGUEM: RecorteDoPainel = {
  id: 'esperando-alguem',
  rotulo: 'Esperando alguém agir',
  ajuda: 'Enviadas, devolvidas para ajuste ou aprovadas sem implantação: todas dependem de um próximo passo.',
  status: ['SOLICITADA', 'EM_AVALIACAO_CAB', 'AGUARDANDO_AJUSTES', 'APROVADA'],
  origem: SO_O_PROCESSO,
  acento: 'bg-amber-400',
}

/**
 * Os critérios completos de um recorte, no formato que a consulta da listagem espera.
 *
 * @param recorte o recorte do painel
 * @param tamanho itens por página — 1 quando só se quer o total
 * @returns os critérios da lista
 */
export function filtrosDoRecorte(recorte: RecorteDoPainel, tamanho: number): FiltrosDaLista {
  return {
    status: recorte.status,
    prioridade: [],
    origem: recorte.origem,
    chamado: '',
    q: '',
    pagina: 1,
    tamanho,
  }
}

/**
 * O endereço da lista já filtrada pelo recorte.
 *
 * O cartão é um link e nada mais: o filtro mora na URL da listagem (`filtros.ts`), então não há
 * uma segunda implementação de filtro no painel para divergir da primeira. De quebra, o endereço
 * é colável no e-mail com o recorte inteiro — inclusive a procedência, que é o que garante que a
 * lista aberta pelo cartão conte as mesmas solicitações que o cartão contou.
 *
 * @param recorte o recorte do painel
 * @returns o caminho da listagem com o recorte na query string
 */
export function enderecoDaLista(recorte: RecorteDoPainel): string {
  return `/gmuds?${escreverNaUrl(filtrosDoRecorte(recorte, TAMANHO_PADRAO)).toString()}`
}
